import { Link } from "react-router-dom";
import { MapPin, ArrowRight, ChevronRight } from "lucide-react";
import SEO from "../components/SEO";
import { SITE } from "../lib/site";
import { CITY_CONTENT, CITY_SLUGS } from "../lib/cityContent";

const HEADQUARTERS_SLUG = "deland";

export default function ServiceArea() {
  return (
    <>
      <SEO
        title="Service Area — Central Florida Crane Rental"
        description="Mobile crane services from DeLand to Orlando, Daytona Beach, Jacksonville, Titusville and 8 more Central Florida cities. Same-day dispatch, 24/7 emergency."
      />

      <section className="relative border-b border-[var(--ac-border)] bg-black overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-14 md:py-28 relative">
          <div className="overline mb-4">Service Area</div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
            Headquartered in DeLand.<br />
            <span className="text-[var(--ac-red)]">Working all of Central Florida.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl text-lg leading-relaxed">
            Centrally located between Orlando and Daytona Beach, we mobilize quickly across
            Volusia, Orange, Seminole, Brevard, Flagler, Osceola and Duval counties.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 mb-12">
            <div className="md:col-span-5">
              <div className="overline mb-4">Cities We Serve</div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl mb-5 leading-tight">
                12 Central Florida cities. One phone number.
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Click any city for local coverage details, drive times, common project types
                and city-specific FAQs. Don&apos;t see yours? Call us — if it&apos;s drivable
                from DeLand, we&apos;ve probably already been there.
              </p>
              <Link to="/contact" className="btn-primary" data-testid="service-area-cta">
                Check Availability <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 border-t border-l border-[var(--ac-border)]">
              {CITY_SLUGS.map((slug) => {
                const c = CITY_CONTENT[slug];
                const isHq = slug === HEADQUARTERS_SLUG;
                return (
                  <Link
                    key={slug}
                    to={`/service-area/${slug}`}
                    data-testid={`area-${slug}`}
                    className="hover-card-border border-r border-b border-[var(--ac-border)] p-5 bg-[var(--ac-bg)] group block"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display text-xl leading-tight">{c.name}</div>
                        <div className="overline mt-1 !text-[10px] truncate">{c.county}</div>
                      </div>
                      <MapPin className="w-4 h-4 text-[var(--ac-red)] mt-1 shrink-0" />
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="font-mono-data text-[10px] text-zinc-500 uppercase tracking-wider">
                        {c.distance}
                      </div>
                      {isHq && (
                        <span className="font-mono-data text-[10px] uppercase tracking-wider text-[var(--ac-red)] border border-[var(--ac-red)] px-2 py-0.5">
                          HQ
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--ac-red)] opacity-0 group-hover:opacity-100 transition-opacity">
                      View coverage <ChevronRight className="w-3 h-3" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--ac-bg-2)] border-y border-[var(--ac-border)] py-14 md:py-16">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="md:col-span-7">
            <h3 className="font-display text-3xl md:text-4xl">
              Outside the listed cities? <span className="text-[var(--ac-red)]">We still travel.</span>
            </h3>
            <p className="text-zinc-400 mt-3 max-w-2xl">
              Long-haul or out-of-market pick? Call dispatch and we&apos;ll quote the mob/demob.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <a href={`tel:${SITE.phoneE164}`} className="btn-primary justify-center" data-testid="service-area-call">
              Call {SITE.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
